import { HOURS_PER_DAY } from '../../constants';
import type { DateIso } from '../../types/domain';
import { nativeQuota } from '../budget/quotas';
import { contractMonthsElapsed, type ContractYear } from '../dates';

/** Consommation d'un quota, dans son unité (h ou j). consumed = null tant que Jira n'est pas chargé
    ou s'il est indisponible ; loading distingue l'attente (« … ») de l'absence de donnée (« — »). */
export interface QuotaUse {
  consumed: number | null;
  quota: number;
  unit: 'h' | 'j';
  pct: number | null;
  alert: boolean;
  labelReady: boolean;
  loading: boolean;
}

/** Quota annuel : consommé de l'année de contrat face au quota de l'année, et au quota échu à
    date (mois de l'année déjà commencés). */
export interface YearQuotaUse extends QuotaUse {
  year: ContractYear | null;
  due: number | null;
  ahead: boolean;
  /** Report de l'année précédente, compris dans le quota (et dans l'échu, dès le début). */
  carried: number;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

export function quotaUse(
  hours: number | null,
  qte: unknown,
  unite: string,
  months: number,
  loading = false,
  carried = 0,
): QuotaUse {
  const isDays = unite.startsWith('jours');
  const quota = round1(nativeQuota(qte, unite, months) + carried);
  const consumed = hours == null ? null : round1(isDays ? hours / HOURS_PER_DAY : hours);
  const pct = consumed != null && quota > 0 ? Math.round((consumed / quota) * 100) : null;
  return {
    consumed,
    quota,
    unit: isDays ? 'j' : 'h',
    pct,
    alert: !!pct && pct > 100,
    labelReady: hours != null,
    loading,
  };
}

/* Les heures de maintenance se reportent d'un mois sur l'autre dans l'année de contrat : on peut
   consommer plus vite que le quota mensuel, mais au-delà du quota échu à date il faut prévenir le client (il va épuiser
   son quota avant la fin de l'année). Au-delà du quota annuel : alerte. */
export function yearQuotaUse(
  hours: number | null,
  qte: unknown,
  unite: string,
  year: ContractYear | null,
  todayIso: DateIso,
  loading = false,
  carried = 0,
): YearQuotaUse {
  const months = year ? year.months : 12;
  const use = quotaUse(year ? hours : null, qte, unite, months, loading, carried);
  const elapsed = year ? contractMonthsElapsed(year.from, year.to, todayIso) : 0;
  const due = year ? round1(nativeQuota(qte, unite, elapsed) + carried) : null;
  const ahead = !use.alert && use.consumed != null && due != null && use.consumed > due;
  return { ...use, year, due, ahead, carried };
}

// Avertissement d'un quota annuel, le même sur la fiche et sur la liste des projets. « Consommation
// rapide » plutôt que « en avance », qui se confondrait avec l'avancement d'un projet (14/10/2026).
export function yearQuotaNote(use: YearQuotaUse): string | null {
  if (use.alert) return "Quota de l'année dépassé : prévenir le client";
  if (use.ahead)
    return `Consommation rapide (${use.due}${use.unit} échus à date) : prévenir le client`;
  return null;
}
